import { registerWidget, type CellContentProps, type CellEditorProps } from '../registry'
import { controlChrome } from '../editorChrome'
import { Checkbox } from '../Checkbox'

/**
 * E7 · boolean content — a read-only checkbox indicator via the shared
 * Checkbox primitive (same visual as the primary column's row-select).
 * Filled cobalt + check when true, empty box when false, em-dash when unset.
 */
function isTrue(value: unknown): boolean {
  return value === true || value === 'true' || value === 1
}

function BoolContent({ value }: CellContentProps) {
  if (value == null || value === '') {
    return <span className="text-muted-foreground">—</span>
  }
  return <Checkbox checked={isTrue(value)} />
}

/**
 * boolean filter editor — a true/false toggle used in filter context (the
 * CHECKBOX_OPERATORS `eq` operator needs a value editor). In cell context the
 * `toggle` field takes precedence and no editor is mounted.
 */
function BoolEditor({ value, onCommit, onCancel, context }: CellEditorProps) {
  const current = isTrue(value)
  return (
    <button
      type="button"
      data-testid="cell-editor"
      className={`flex w-full items-center gap-1.5 rounded-sm px-2 py-1.5 text-left text-sm text-foreground outline-none ${controlChrome(context)}`}
      onClick={() => onCommit(!current)}
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          e.preventDefault()
          onCancel()
        }
      }}
    >
      <Checkbox checked={current} />
      <span>{current ? 'True' : 'False'}</span>
    </button>
  )
}

// A checkbox has no intermediate edit state — double-click flips it directly.
// `write` is also provided for filter-context value editing (toggling true/false
// inside a filter rule). Cell.tsx uses `toggle` first, so write is filter-only.
registerWidget('checkbox', {
  read: BoolContent,
  write: BoolEditor,
  toggle: (value) => !isTrue(value),
  align: 'center',
  defaultWidth: 90,
})
